import { VarianceNode } from '../../../../types/VarianceNode';
import { useNodeOperations } from '../../../../contexts/NodeOperationsContext';
import { useNodePreview } from '../../../../hooks/useNodePreview';

interface Props {
    node: VarianceNode;
}

const MAX_PARAMS = 3;

import "./ComponentCard.css";

export function VarianceComponentCard({ node }: Props) {
    const { globalStart, globalEnd, isInteractionLocked, updateNodeMetadata, updatePreviewResult } = useNodeOperations();
    const { config } = node;
    const parameters: any[] = config.parameters ?? [];

    const {
        isCalculating,
        errorMsg,
        result,
        calculatePreview: triggerCalculate
    } = useNodePreview<any>(node, globalStart || null, globalEnd || null, updateNodeMetadata, updatePreviewResult);

    const stopPropagation = (e: React.UIEvent) => e.stopPropagation();

    const handleCalculate = async () => {
        if (isInteractionLocked) {
            return;
        }
        if (!config.target) {
            alert('Connect a Target series first.');
            return;
        }
        if (parameters.length === 0) {
            alert('Connect at least one Parameter series.');
            return;
        }
        triggerCalculate();
    };

    const barColors = ['var(--accent-primary)', 'var(--accent-success)', 'var(--accent-warning)'];
    const maxScore = result ? Math.max(...(result.results as { score: number }[]).map((r) => r.score), 0.001) : 1;

    return (
        <div
            className="internal-card-container"
            style={{ minWidth: '320px', padding: '0.2rem' }}
            onPointerDown={stopPropagation}
            onMouseDown={stopPropagation}
        >
            {/* Status Row */}
            <div className="flex flex-col gap-sm bg-panel p-sm rounded-md border border-surface-glass-border">
                <div className="flex justify-between items-start">
                    <div className="flex flex-col gap-xs">
                        <label className="field-label">Target</label>
                        <span style={{
                            fontSize: '0.85rem',
                            color: config.target ? 'var(--accent-success)' : 'var(--text-muted)',
                            fontWeight: 600,
                            textShadow: config.target ? '0 0 10px var(--accent-success-subtle)' : 'none'
                        }}>
                            {config.target ? (config.target.title || 'Connected') : '— not connected —'}
                        </span>
                    </div>
                    <div style={{ textAlign: 'right' }} className="flex flex-col gap-xs">
                        <label className="field-label">Params ({parameters.length}/{MAX_PARAMS})</label>
                        {parameters.length === 0 ? (
                            <span className="text-muted text-xs">none</span>
                        ) : (
                            <div className="flex flex-col gap-xs items-end">
                                {parameters.map((p) => (
                                    <div key={p.id} className="text-secondary font-medium" style={{ fontSize: '0.75rem' }}>
                                        {p.title || p.type}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Preview Button */}
            <div className="flex justify-end">
                <button
                    onClick={handleCalculate}
                    disabled={isCalculating || isInteractionLocked}
                    className="action-btn pan-zoom-exclude nodrag"
                    style={{ gap: '0.4rem', padding: '0.3rem 0.8rem' }}
                >
                    {isCalculating ? (
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ animation: 'spin 1s linear infinite' }}>
                            <line x1="12" y1="2" x2="12" y2="6"></line>
                            <line x1="12" y1="18" x2="12" y2="22"></line>
                            <line x1="4.93" y1="4.93" x2="7.76" y2="7.76"></line>
                            <line x1="16.24" y1="16.24" x2="19.07" y2="19.07"></line>
                            <line x1="2" y1="12" x2="6" y2="12"></line>
                            <line x1="18" y1="12" x2="22" y2="12"></line>
                            <line x1="4.93" y1="19.07" x2="7.76" y2="16.24"></line>
                            <line x1="16.24" y1="4.93" x2="19.07" y2="7.76"></line>
                        </svg>
                    ) : (
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="11" cy="11" r="8"></circle>
                            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                        </svg>
                    )}
                    <span>{isCalculating ? 'Analysing…' : 'Analyse'}</span>
                </button>
            </div>

            {/* Error */}
            {errorMsg && (
                <div className="nature-warning-box">
                    {errorMsg}
                </div>
            )}

            {/* Attribution Preview */}
            {result && result.results.length > 0 && (
                <div className="flex flex-col gap-md bg-panel p-md rounded-md border border-surface-glass-border shadow-lg">
                    <div className="flex flex-col gap-sm">
                        {result.results.map((r: any, i: number) => (
                            <div key={r.label} className="flex flex-col gap-xs">
                                <div className="flex justify-between items-center">
                                    <span className="text-secondary font-semibold" style={{ fontSize: '0.8rem' }}>
                                        {r.label}
                                    </span>
                                    <span style={{ fontSize: '0.85rem', color: barColors[i % barColors.length], fontWeight: 700 }}>
                                        {(r.score * 100).toFixed(1)}%
                                    </span>
                                </div>
                                <div style={{ height: '6px', borderRadius: '3px', background: 'var(--surface-overlay-subtle)', overflow: 'hidden' }}>
                                    <div style={{
                                        height: '100%',
                                        width: `${(r.score / maxScore) * 100}%`,
                                        background: `linear-gradient(to right, ${barColors[i % barColors.length]}, transparent)`,
                                        borderRadius: '3px',
                                        transition: 'width 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
                                        boxShadow: `0 0 10px ${barColors[i % barColors.length]}44`
                                    }} />
                                </div>
                            </div>
                        ))}
                    </div>
                    <p className="text-muted italic" style={{ fontSize: '0.75rem', lineHeight: '1.5', borderTop: '1px solid var(--surface-overlay-subtle)', paddingTop: '0.75rem' }}>
                        {result.explanation}
                    </p>
                </div>
            )}

            {/* Empty placeholder */}
            {!result && !errorMsg && (
                <div style={{
                    height: '100px',
                    border: '1px dashed var(--surface-glass-border)',
                    borderRadius: 'var(--radius-md)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: 0.4,
                    gap: '0.5rem',
                    background: 'var(--surface-overlay-min)'
                }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 12V7a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h7"></path>
                        <line x1="3" y1="10" x2="21" y2="10"></line>
                        <circle cx="18" cy="18" r="3"></circle>
                        <line x1="20" y1="20" x2="22" y2="22"></line>
                    </svg>
                    <span className="text-center px-lg" style={{ fontSize: '0.75rem' }}>
                        Connect target + parameters to begin analysis
                    </span>
                </div>
            )}
        </div>
    );
}
